import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismCard,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import SkeletonTableIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-table.svg?raw';
import HotelBenchmarkInsightChartsLosChart from 'lumen/components/hotel/benchmark-insight/charts/los-chart';
import {
  OCCUPANCY_VIEWS,
  type OccupancyView,
} from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import type { SectionHeaderBreadcrumb } from 'lumen/components/hotel/benchmark-insight/sections/section-header-breadcrumbs';
import HotelBenchmarkInsightSectionsSectionHeaderBreadcrumbs from 'lumen/components/hotel/benchmark-insight/sections/section-header-breadcrumbs';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import HotelBenchmarkInsightTablesBeniTable from 'lumen/components/hotel/benchmark-insight/tables/beni-table';
import type {
  BeniBreakdown,
  BeniDataApiResponse,
  BeniMeta,
  BeniMetaComparisonSet,
} from 'lumen/models';
import {
  BREAKDOWN_PERIOD_TYPES,
  type BreakdownPeriodType,
} from 'lumen/models/benchmark-insight/breakdown';
import {
  type BeniBreakdownType,
  BREAKDOWNS,
  type Kpi,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import {
  DROPDOWN_LOS_SORT_OPTIONS,
  type DropdownLosSortOption,
} from 'lumen/utils/constants/benchmark-insight/los/dropdown-los';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';

interface HotelBenchmarkInsightSectionsSectionLosSectionLosSignature {
  Args: {
    apiResponse: BeniDataApiResponse;
    breakdownType: BeniBreakdownType;
    breakdownsByComparisonSet: Record<ComparisonSet, BeniBreakdown[]>;
    changeSelectedLosSortOption: (option: DropdownLosSortOption) => void;
    changeSelectedOccupancyView: (view: OccupancyView) => void;
    isExpanded: boolean;
    isLoading: boolean;
    los: string | null;
    meta: BeniMeta;
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation: string;
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    primaryComparisonSetType: ComparisonSet;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    secondaryComparisonSetType: ComparisonSet;
    selectedKpiLosChart: Kpi;
    selectedLosSortOption: DropdownLosSortOption;
    selectedOccupancyView: OccupancyView;
    selectedRange: DateRangeType;
    setLos: (los: string | null) => void;
    setSelectedKpiLosChart: (kpi: Kpi) => void;
    showSkeleton?: boolean;
    showSkeletonAnimation?: boolean;
    varianceTranslation: string;
  };
  Blocks: {
    expandButton: [];
  };
}

export default class HotelBenchmarkInsightSectionsSectionLosSectionLos extends Component<HotelBenchmarkInsightSectionsSectionLosSectionLosSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('benchmark-insight/beni-table-service')
  declare benchmarkInsightBeniTableService: Services['benchmark-insight/beni-table-service'];
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  breakdown = BREAKDOWNS.LENGTH_OF_STAY;

  exportLosTableTask = task({}, async (): Promise<void> => {
    const { selectedRange } = this.args;
    const { columns, rows, breakdown } = this;

    await this.benchmarkInsightBeniTableService.exportTableTask.perform({
      columns,
      rows,
      breakdown,
      dateRange: selectedRange,
    });

    this.usageAnalytics.track('Table Exported', {
      eventTrigger: 'los',
      product: 'benchmark-insight',
    });
  });

  get breadcrumbs(): SectionHeaderBreadcrumb[] {
    const breadcrumbs: {
      id: string | null;
      isClickable: boolean;
      isTopLevel?: boolean;
      label: string;
    }[] = [];
    const { los } = this.args;

    if (!los) {
      return [];
    }

    breadcrumbs.push({
      id: null,
      label: this.intl.t('benchmark_insight.los.breakdown-title'),
      isClickable: true,
      isTopLevel: true,
    });

    breadcrumbs.push({
      id: los,
      label: this.intl.t('general.los', { los }),
      isClickable: false,
    });

    return breadcrumbs;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = this.benchmarkInsightBeniTableService.getColumns({
      primaryComparisonSetType: this.args.primaryComparisonSetType,
      secondaryComparisonSetType: this.args.secondaryComparisonSetType,
      ownComparisonSetMeta: this.ownComparisonSetMeta,
      primaryComparisonSetMeta: this.primaryComparisonSetMeta,
      secondaryComparisonSetMeta: this.secondaryComparisonSetMeta,
      breakdown: BREAKDOWNS.LENGTH_OF_STAY,
      dateAggregation: DATE_AGGREGATION.DAY,
      selectedRange: this.args.selectedRange,
      referencePeriod: this.args.referencePeriod,
      visibleKpis: this.visibleKpis,
    });

    return columns;
  }

  get isExportButtonDisabled(): boolean {
    return (
      this.args.showSkeleton ||
      this.exportLosTableTask.isRunning ||
      this.benchmarkInsightFeatures.isBenchmarkInsightLight
    );
  }

  get isFiltering(): boolean {
    return this.args.los !== null;
  }

  get ownComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      BENI_COMPARISON_SETS.OWN,
    );
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  get primaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, primaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      primaryComparisonSetType,
    );
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const rows = this.benchmarkInsightBeniTableService.getRows(
      this.ownComparisonSetBreakdowns,
      this.primaryComparisonSetBreakdowns,
      this.secondaryComparisonSetBreakdowns,
      [],
      BREAKDOWNS.LENGTH_OF_STAY,
      this.filterBreakdownExistingValues.bind(this),
    );

    return rows;
  }

  get secondaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, secondaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      secondaryComparisonSetType,
    );
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  get showMix(): boolean {
    const { selectedKpiLosChart, selectedOccupancyView } = this.args;

    return (
      selectedOccupancyView === OCCUPANCY_VIEWS.MIX &&
      selectedKpiLosChart === KPIS.OCCUPANCY &&
      !this.isFiltering
    );
  }

  get visibleKpis(): Kpi[] {
    const allKpis = Object.values(KPIS);

    return this.showMix
      ? allKpis.filter((kpi) => kpi !== KPIS.OCCUPANCY)
      : allKpis.filter((kpi) => kpi !== KPIS.OCCUPANCY_MIX);
  }

  filterBreakdownExistingValues(breakdown: BeniBreakdown): boolean {
    const breakdownPeriodHasOccupancyValue = (
      period: BreakdownPeriodType,
    ): boolean => {
      const breakdownPeriod =
        breakdown.getBreakdownPeriodByBreakdownPeriodType(period);

      const value = breakdownPeriod.total[KPIS.OCCUPANCY];

      return value !== null && value >= 0.0005;
    };

    let breakdownPeriodTypesToEvaluate = Object.values(BREAKDOWN_PERIOD_TYPES);

    // Don't evaluate the reference period values of the secondary comparison set, because we don't visualize them in the table.
    if (breakdown.comparisonSetType === this.args.secondaryComparisonSetType) {
      breakdownPeriodTypesToEvaluate = [BREAKDOWN_PERIOD_TYPES.CURRENT];
    }

    return breakdownPeriodTypesToEvaluate.some((period) =>
      breakdownPeriodHasOccupancyValue(period),
    );
  }

  getComparisonSetBreakdowns(
    breakdownsByComparisonSet: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
    comparisonSet: ComparisonSet,
  ): BeniBreakdown[] {
    if (!breakdownsByComparisonSet) {
      return [];
    }

    const comparisonSetBreakdowns = breakdownsByComparisonSet[comparisonSet];
    if (!comparisonSetBreakdowns) {
      return [];
    }

    return comparisonSetBreakdowns;
  }

  @action
  changeSelectedKpiLosChart(kpi: Kpi): void {
    const { primaryComparisonSetMeta } = this;
    const { selectedRange, selectedLosSortOption } = this.args;
    const primaryComparisonSetType =
      primaryComparisonSetMeta?.comparisonSetType;

    if (
      selectedLosSortOption !== DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING
    ) {
      const selectedLosSortOptionIsDisabled = primaryComparisonSetType
        ? this.benchmarkInsightBeniService.isHiddenFutureCompsetData(
            primaryComparisonSetType,
            selectedRange.start,
            kpi,
          )
        : false;

      if (selectedLosSortOptionIsDisabled) {
        this.args.changeSelectedLosSortOption(
          DROPDOWN_LOS_SORT_OPTIONS.MY_HIGHEST_PERFORMING,
        );
      }
    }

    this.args.setSelectedKpiLosChart(kpi);
  }

  @action
  onClickBreadcrumb(breadcrumbId: string | null): void {
    const { setLos } = this.args;

    if (breadcrumbId === null) {
      setLos(null);
      return;
    }

    setLos(breadcrumbId);
  }

  @action
  removeFilter(): void {
    this.args.setLos(null);
  }

  <template>
    <PrismCard data-test-id="section-los">
      <:header>
        <div class="flex items-center">
          <HotelBenchmarkInsightSectionsSectionHeaderBreadcrumbs
            @breadcrumbs={{this.breadcrumbs}}
            @onClickBreadcrumb={{this.onClickBreadcrumb}}
            @title={{t "benchmark_insight.los.breakdown-title"}}
          />

        </div>
      </:header>

      <:headerAfter>
        <div class="flex justify-center gap-100">
          {{#if this.benchmarkInsightFeatures.showExportButtons}}
            <div>
              <PrismButton
                @buttonType="secondary"
                @iconBefore="export"
                @isDisabled={{this.isExportButtonDisabled}}
                @isLoading={{this.exportLosTableTask.isRunning}}
                @label={{t "general.export_group.export"}}
                @onClick={{this.exportLosTableTask.perform}}
                class="text-nowrap"
                data-test-id="section-los-export-button"
              />

              {{#if this.benchmarkInsightFeatures.isBenchmarkInsightLight}}
                <PrismTooltip
                  @text={{this.benchmarkInsightFeatures.beniLightSegmentationTooltipText}}
                />
              {{/if}}
            </div>
          {{/if}}

          {{yield to="expandButton"}}
        </div>
      </:headerAfter>

      <:content>
        <div class="flex flex-col gap-400">
          <div class="overflow-hidden">
            <HotelBenchmarkInsightChartsLosChart
              @changeSelectedKpiLosChart={{this.changeSelectedKpiLosChart}}
              @changeSelectedLosSortOption={{@changeSelectedLosSortOption}}
              @changeSelectedOccupancyView={{@changeSelectedOccupancyView}}
              @isExpanded={{@isExpanded}}
              @isFiltering={{this.isFiltering}}
              @los={{@los}}
              @onUpdateChartReference={{@onUpdateChartReference}}
              @ownComparisonSetBreakdowns={{this.ownComparisonSetBreakdowns}}
              @ownComparisonSetMeta={{this.ownComparisonSetMeta}}
              @pickupTranslation={{@pickupTranslation}}
              @primaryComparisonSet={{@primaryComparisonSetType}}
              @primaryComparisonSetBreakdowns={{this.primaryComparisonSetBreakdowns}}
              @primaryComparisonSetMeta={{this.primaryComparisonSetMeta}}
              @referencePeriod={{@referencePeriod}}
              @secondaryComparisonSetBreakdowns={{this.secondaryComparisonSetBreakdowns}}
              @secondaryComparisonSetMeta={{this.secondaryComparisonSetMeta}}
              @selectedKpiLosChart={{@selectedKpiLosChart}}
              @selectedLosSortOption={{@selectedLosSortOption}}
              @selectedOccupancyView={{@selectedOccupancyView}}
              @selectedRange={{@selectedRange}}
              @setLos={{@setLos}}
              @showMix={{this.showMix}}
              @showSkeleton={{@showSkeleton}}
              @showSkeletonAnimation={{@showSkeletonAnimation}}
              @varianceTranslation={{@varianceTranslation}}
              @visibleKpis={{this.visibleKpis}}
            />
          </div>

          {{#if @isExpanded}}
            <div class="flex flex-col gap-400">
              <p class="text-400--bold">
                {{t "general.details"}}
              </p>

              {{#if @showSkeleton}}
                <HotelBenchmarkInsightSkeleton
                  @imageHtml={{SkeletonTableIllustration}}
                  @showAnimation={{@showSkeletonAnimation}}
                />
              {{else}}
                <div class="rounded-200">
                  <HotelBenchmarkInsightTablesBeniTable
                    @columns={{this.columns}}
                    @dataTestId="section-los-table"
                    @rows={{this.rows}}
                  />
                </div>
              {{/if}}
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
